import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Container, Flex, Grid, Link, Spinner } from "flicket-ui";
import { NextPage } from "next";
import { css } from "styled-components";
import useSWR from "swr";
import { Seo } from "~components";
import { useOrganization, useShopifySDK } from "~hooks";
import { handleNotFoundRedirect } from "~lib";
import { MerchandiseLayout, ProductCard } from "~components/merchandise";
import { getInitialPropsWithShopifySDK } from "~lib/shopify/client";
import { ProductsQuery } from "~lib/shopify/sdk";
import { getDiscountPercentage, renderPriceRange } from "~lib/shopify/utils";

//page size increased to 100 items as ordering by collections means the product array tries to load the next times however, given that the items are out of order
//it tries to load the incorrect next set of items
const PAGE_SIZE = 100;

const MerchandisePage: NextPage<{ products: ProductsQuery }> = (props) => {
  const [products, setProducts] = useState(props.products);
  const [collectionItems, setCollectionItems] = useState([]);
  const { organization } = useOrganization();
  const [loadingMore, setLoadingMore] = useState(false);
  const sdk = useShopifySDK();
  const loader = useRef(null);

  const { data } = useSWR(
    loadingMore
      ? ["merchandise", products?.products?.edges?.slice(-1)?.[0]?.cursor]
      : null,
    async (_, after) => sdk.products({ first: PAGE_SIZE, after })
  );

  const { data: collections } = useSWR(["collections"], async (_, after) =>
    sdk.collection({ first: 100 })
  );

  products.products.edges.sort((a, b) => {
    return (
      collectionItems.indexOf(a.node.id) - collectionItems.indexOf(b.node.id)
    );
  });

  useEffect(() => {
    if (collections) {
      const idArray = [];
      collections?.collections?.edges[0]?.node?.products?.edges.forEach(
        (node) => idArray.push(node.node.id)
      );
      setCollectionItems(idArray);
    }
  }, [collections]);

  useEffect(() => {
    if (collectionItems.length > 0) {
      products.products.edges.sort((a, b) => {
        return collectionItems.indexOf(b) - collectionItems.indexOf(a);
      });
    }
  }, [collectionItems]);

  useEffect(() => {
    if (data) {
      setProducts((productsQuery) => ({
        ...productsQuery,
        products: {
          pageInfo: data?.products?.pageInfo,
          edges: productsQuery?.products?.edges?.concat(data?.products?.edges),
        },
      }));
      setLoadingMore(false);
    }
  }, [data]);

  const observerHandler = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && products?.products?.pageInfo?.hasNextPage) {
        setLoadingMore(true);
      }
    },
    [products]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(observerHandler, {
      root: null,
      rootMargin: "200px",
      threshold: 1.0,
    });
    if (loader.current) {
      observer.observe(loader.current);
    }
  }, []);

  return (
    <>
      <Seo title="Merchandise overview" description={undefined} />
      <MerchandiseLayout title="Merchandise">
        <Container>
          <Grid rowSize={[1, null, 2, 4]} columnGap={3} rowGap={3}>
            {products?.products?.edges?.map(({ node }) => {
              const discount = Math.max(
                getDiscountPercentage(
                  node?.priceRange?.maxVariantPrice,
                  node?.compareAtPriceRange?.maxVariantPrice
                ),
                getDiscountPercentage(
                  node?.priceRange?.minVariantPrice,
                  node?.compareAtPriceRange?.minVariantPrice
                )
              );

              return (
                <Link
                  css={css({
                    "& > *": { height: "100%" },
                  })}
                  key={node.id}
                  to="/merchandise/[handle]"
                  linkAs={`/merchandise/${node.handle}`}
                >
                  <Box
                    css={css({
                      "& > *": { height: "100%" },
                    })}
                  >
                    <ProductCard
                      badge={discount ? `Up to -${discount}%` : null}
                      title={node.title}
                      imgSrc={node?.images?.edges?.[0]?.node?.url}
                      inStock={node?.availableForSale}
                      price={renderPriceRange(
                        node.priceRange,
                        organization.currency
                      )}
                    />
                  </Box>
                </Link>
              );
            })}
          </Grid>
        </Container>
        <Flex ref={loader} my={2} mb={{ _: 10, md: 0 }} variant="center">
          <Spinner
            css={{ visibility: loadingMore ? "visible" : "hidden" }}
            color="P300"
            size={48}
          />
        </Flex>
      </MerchandiseLayout>
    </>
  );
};

MerchandisePage.getInitialProps = getInitialPropsWithShopifySDK(async (ctx) => {
  const { sdk } = ctx;
  const products = await sdk.products({ first: PAGE_SIZE });

  if (!products.products) {
    return handleNotFoundRedirect(ctx);
  }

  return {
    products,
  };
});

export default MerchandisePage;
